"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { scrollToHomepageSection } from "@/lib/layout/learn-section";
import { LEARN_NAV, isLearnSectionPage } from "@/lib/layout/site-nav";

type DesktopLearnMenuProps = {
  pathname: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function DesktopLearnMenu({
  pathname,
  open,
  onOpenChange,
}: DesktopLearnMenuProps) {
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm font-semibold leading-6 text-foreground hover:text-primary"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => onOpenChange(!open)}
      >
        Learn
        <ChevronDownIcon
          className="h-4 w-4 text-text-light"
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 z-10 mt-2 w-48 origin-top-left overflow-hidden rounded-md bg-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          {LEARN_NAV.map((item) => (
            <Link
              key={item.sectionId}
              href={item.href}
              role="menuitem"
              className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
              onClick={(event) => {
                onOpenChange(false);
                if (!isLearnSectionPage(pathname)) return;
                event.preventDefault();
                if (!scrollToHomepageSection(item.sectionId)) {
                  router.push(item.href);
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
